/**
 * Documentation content model.
 *
 * Pages are authored as typed data, not Markdown: the landing app ships no
 * Markdown runtime, and typed blocks let the build emit three views of one
 * source — rendered HTML, a `.md` twin for agent clients, and JSON-LD.
 *
 * Text fields accept a small inline syntax rendered by `renderInline`
 * (components/docs/inline.tsx): `[label](/href)`, `` `code` ``, `**strong**`.
 * Nothing else is interpreted, and nothing is ever injected as raw HTML.
 *
 * Heading rule: a page's body starts at `h2` and never skips a level.
 * Sequential heading structure is what lets answer engines attribute an
 * extracted passage to the section it actually came from.
 */

/** Inline-markup string. The alias marks which fields accept the syntax. */
export type RichText = string;

export type DocStep = {
  /** Imperative and specific: "Copy your server URL", not "Step 2". */
  title: string;
  text: RichText;
  /** Optional literal the reader types or pastes at this step. */
  code?: string;
};

export type DocFaqItem = {
  /** Phrased as a reader would type it. Becomes one FAQPage entry. */
  q: string;
  a: RichText;
};

export type DocDefinition = {
  term: string;
  /** Self-contained: readable without the surrounding page. */
  def: RichText;
};

export type DocBlock =
  /** Front-loaded user-facing summary. Exactly one per page, immediately after the H1. */
  | { type: "lead"; text: RichText }
  | { type: "p"; text: RichText }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: RichText[] }
  | { type: "ol"; items: RichText[] }
  /** Ordered procedure. Emits HowTo JSON-LD when it is the page's only steps block. */
  | { type: "steps"; items: DocStep[] }
  | { type: "table"; caption?: string; headers: string[]; rows: RichText[][] }
  | { type: "code"; language: string; code: string; caption?: string }
  /** `limits` renders the "What this does not do" treatment. */
  | { type: "callout"; variant: "info" | "warning" | "limits"; title?: string; text: RichText }
  /** Emits FAQPage JSON-LD, and renders its own h2. At most one per page. */
  | { type: "faq"; title?: string; items: DocFaqItem[] }
  | { type: "dl"; items: DocDefinition[] };

export type DocPage = {
  slug: string;
  /** Concise H1 and <title> base, named for a feature, system, or user flow. */
  title: string;
  /**
   * Short label for the sidebar, cards, and pager. Keep it to 2-5 words.
   * Falls back to `title`.
   */
  navLabel?: string;
  /** Meta description and card subtitle. One sentence, 110-160 characters. */
  description: string;
  /** Owning section id from DOC_SECTIONS. */
  section: string;
  /** ISO date this guide first joined the current public documentation set. */
  published: string;
  /** ISO date of the last substantive factual change. Drives dateModified. */
  updated: string;
  /**
   * Search phrases and user jobs covered by this guide. Never emitted as
   * keyword meta; they only improve local search and preserve editorial scope.
   */
  targets?: readonly string[];
  /** Slugs of adjacent product systems or next steps. */
  related?: readonly string[];
  blocks: DocBlock[];
};

export type DocSection = {
  id: string;
  label: string;
  /** One line naming the reader's job in this section. Shown on the hub. */
  blurb: string;
  pages: string[];
};

/** Heading blocks in document order, for the on-page table of contents. */
export function headingBlocks(
  blocks: readonly DocBlock[],
): Extract<DocBlock, { type: "h2" | "h3" }>[] {
  return blocks.filter(
    (b): b is Extract<DocBlock, { type: "h2" | "h3" }> => b.type === "h2" || b.type === "h3",
  );
}

/**
 * Every piece of prose on a block, flattened. The old search indexed only
 * `b.text`, which silently dropped all list, table, and FAQ content — about
 * half the corpus was unsearchable.
 */
export function blockText(block: DocBlock): string {
  switch (block.type) {
    case "lead":
    case "p":
    case "h2":
    case "h3":
      return block.text;
    case "ul":
    case "ol":
      return block.items.join(" ");
    case "steps":
      return block.items.map((s) => `${s.title} ${s.text}`).join(" ");
    case "table":
      return [block.caption ?? "", ...block.headers, ...block.rows.flat()].join(" ");
    case "code":
      return block.caption ?? "";
    case "callout":
      return `${block.title ?? ""} ${block.text}`;
    case "faq":
      return block.items.map((i) => `${i.q} ${i.a}`).join(" ");
    case "dl":
      return block.items.map((i) => `${i.term} ${i.def}`).join(" ");
  }
}
